import Phaser from 'phaser';
import { TILE_SIZE } from '../layout';

/**
 * Game feel: small, soft effects that make mowing satisfying without getting busy. All one-shot
 * bursts from pooled emitters; screen shake honors the reduced-shake setting.
 */

export const FX_KEY = 'fx';
const FRAME = {
  clipLight: 0,
  clipDark: 1,
  sparkle: 2,
  dust: 3,
  partLight: 4,
  partDark: 5,
  petal: 6,
  // Appended (art sheets follow this order): grass blades, a slanted sliver lit and dark.
  bladeLight: 7,
  bladeDark: 8,
  // A bee: a yellow speck with a dark stripe.
  bee: 9,
} as const;
export { FRAME as FX_FRAME };
const CELL = 4;

export function createFxTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(FX_KEY)) return;
  const tex = scene.textures.createCanvas(FX_KEY, CELL * 10, CELL);
  if (!tex) throw new Error('Could not create fx texture');
  const ctx = tex.context;
  const px = (frame: number, x: number, y: number, color: string) => {
    ctx.fillStyle = color;
    ctx.fillRect(frame * CELL + x, y, 1, 1);
  };
  const block = (frame: number, color: string) => {
    for (const [x, y] of [
      [1, 1],
      [2, 1],
      [1, 2],
      [2, 2],
    ] as const) {
      px(frame, x, y, color);
    }
  };
  block(FRAME.clipLight, '#c8f07a');
  block(FRAME.clipDark, '#5fa543');
  for (const [x, y] of [
    [1, 0],
    [0, 1],
    [1, 1],
    [2, 1],
    [1, 2],
  ] as const) {
    px(FRAME.sparkle, x, y, '#fff6c2');
  }
  block(FRAME.dust, '#c9b48a');
  block(FRAME.partLight, '#9aa0a6');
  block(FRAME.partDark, '#3a3b3f');
  block(FRAME.petal, '#ff7aa0');
  px(FRAME.petal, 1, 1, '#ffd0de');
  for (const [frame, color] of [
    [FRAME.bladeLight, '#e4fb9c'],
    [FRAME.bladeDark, '#6fbf4a'],
  ] as const) {
    px(frame, 3, 0, color);
    px(frame, 2, 1, color);
    px(frame, 1, 2, color);
    px(frame, 1, 3, color);
  }
  px(FRAME.bee, 1, 1, '#ffd23f');
  px(FRAME.bee, 2, 1, '#10180c');
  px(FRAME.bee, 1, 0, '#e8f6ff');
  tex.refresh();
  for (let i = 0; i < 10; i++) tex.add(i, 0, i * CELL, 0, CELL, CELL);
}

export class Effects {
  private readonly sparkles: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly dust: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly parts: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly confetti: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly petals: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly spray: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly bagPuff: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly bees: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly exhaustPuffs: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly trackDepth: number;

  constructor(
    private readonly scene: Phaser.Scene,
    depth: number,
    private readonly reducedShake: boolean,
  ) {
    createFxTexture(scene);
    this.sparkles = scene.add
      .particles(0, 0, FX_KEY, {
        frame: FRAME.sparkle,
        speed: { min: 12, max: 28 },
        angle: { min: 0, max: 360 },
        lifespan: 380,
        alpha: { start: 1, end: 0 },
        emitting: false,
      })
      .setDepth(depth + 2);
    this.dust = scene.add
      .particles(0, 0, FX_KEY, {
        frame: FRAME.dust,
        speed: { min: 6, max: 16 },
        angle: { min: 0, max: 360 },
        lifespan: 450,
        alpha: { start: 0.9, end: 0 },
        scale: { start: 1, end: 2 },
        emitting: false,
      })
      .setDepth(depth + 1);
    this.parts = scene.add
      .particles(0, 0, FX_KEY, {
        frame: [FRAME.partLight, FRAME.partDark],
        speed: { min: 30, max: 70 },
        angle: { min: 200, max: 340 },
        gravityY: 160,
        lifespan: 700,
        alpha: { start: 1, end: 0 },
        rotate: { min: 0, max: 360 },
        emitting: false,
      })
      .setDepth(depth + 2);
    this.trackDepth = depth - 2;
    // Clippings thrown out of the mower's discharge (aimed per burst): specks and blades that arc
    // up and fall back to the lawn, tumbling.
    this.spray = scene.add
      .particles(0, 0, FX_KEY, {
        frame: [FRAME.clipLight, FRAME.clipDark, FRAME.bladeLight, FRAME.bladeDark],
        speed: { min: 34, max: 82 },
        gravityY: 170,
        lifespan: { min: 300, max: 600 },
        alpha: { start: 1, end: 0.2 },
        scale: { start: 1.25, end: 0.8 },
        rotate: { min: 0, max: 360 },
        emitting: false,
      })
      .setDepth(depth + 1);
    // A swarm: bees buzzing about whatever they follow.
    this.bees = scene.add
      .particles(0, 0, FX_KEY, {
        frame: FRAME.bee,
        speed: { min: 14, max: 34 },
        angle: { min: 0, max: 360 },
        lifespan: { min: 260, max: 460 },
        frequency: 30,
        quantity: 2,
        emitting: false,
      })
      .setDepth(depth + 3);
    // A grass bag's breath: a few clippings puffing gently out of it.
    this.bagPuff = scene.add
      .particles(0, 0, FX_KEY, {
        frame: [FRAME.clipLight, FRAME.bladeLight, FRAME.bladeDark],
        speed: { min: 6, max: 18 },
        gravityY: 40,
        lifespan: { min: 240, max: 420 },
        alpha: { start: 0.9, end: 0 },
        rotate: { min: 0, max: 360 },
        emitting: false,
      })
      .setDepth(depth + 1);
    // Soft exhaust from gas mowers.
    this.exhaustPuffs = scene.add
      .particles(0, 0, FX_KEY, {
        frame: FRAME.dust,
        speed: { min: 3, max: 8 },
        angle: { min: 240, max: 300 },
        lifespan: 650,
        alpha: { start: 0.55, end: 0 },
        scale: { start: 0.8, end: 2.2 },
        emitting: false,
      })
      .setDepth(depth + 1);
    // Petals knocked loose when the mower runs over a flower bed.
    this.petals = scene.add
      .particles(0, 0, FX_KEY, {
        frame: [FRAME.petal, FRAME.clipDark],
        speed: { min: 20, max: 55 },
        angle: { min: 0, max: 360 },
        gravityY: 60,
        lifespan: { min: 380, max: 700 },
        alpha: { start: 1, end: 0 },
        rotate: { min: 0, max: 360 },
        emitting: false,
      })
      .setDepth(depth + 2);
    // Lawn-complete confetti: clippings and sparkles drifting down, tumbling.
    this.confetti = scene.add
      .particles(0, 0, FX_KEY, {
        frame: [FRAME.clipLight, FRAME.clipDark, FRAME.sparkle],
        speedY: { min: 18, max: 46 },
        speedX: { min: -16, max: 16 },
        gravityY: 30,
        lifespan: { min: 1300, max: 2000 },
        alpha: { start: 1, end: 0 },
        scale: { min: 1, max: 1.5 },
        rotate: { min: 0, max: 360 },
        emitting: false,
      })
      .setDepth(60);
  }

  /**
   * Clippings thrown from a freshly cut tile: out of the right-hand side (rotary mowers' discharge
   * chute), or forward for the reel. `headingDeg` uses Phaser's angles (0 = east, 90 = south).
   */
  sprayClippings(tileX: number, tileY: number, headingDeg: number, forward: boolean): void {
    const { x, y } = center(tileX, tileY);
    this.throwClippings(x, y, forward ? headingDeg : headingDeg + 90, 5);
  }

  /**
   * `count` clippings thrown from (x, y) toward `aimDeg` (Phaser's angles), a little upward so
   * they arc before they fall; `bag` puffs them gently instead (a grass bag).
   */
  throwClippings(x: number, y: number, aimDeg: number, count: number, bag = false): void {
    if (bag) {
      this.bagPuff.setEmitterAngle({ min: aimDeg - 40, max: aimDeg + 40 });
      this.bagPuff.explode(Math.max(1, Math.round(count / 3)), x, y);
      return;
    }
    // A sideways throw is tilted toward the top of the screen (up, in the three-quarter view) so
    // it arcs; one thrown straight toward or away from us is left to gravity.
    const vertical = Math.abs(aimDeg - 90) <= 15 || Math.abs(aimDeg - 270) <= 15;
    const aim = vertical ? aimDeg : aimDeg > 90 && aimDeg < 270 ? aimDeg + 18 : aimDeg - 18;
    this.spray.setEmitterAngle({ min: aim - 24, max: aim + 24 });
    this.spray.explode(count, x, y);
  }

  /** Faint wheel tracks along the direction of travel on a fresh cut; they fade out. */
  wheelTracks(tileX: number, tileY: number, vertical: boolean): void {
    const x = tileX * TILE_SIZE;
    const y = tileY * TILE_SIZE;
    for (const offset of [4, 11]) {
      const line = vertical
        ? this.scene.add.rectangle(x + offset, y, 1, TILE_SIZE, 0x0f3a16, 0.45)
        : this.scene.add.rectangle(x, y + offset, TILE_SIZE, 1, 0x0f3a16, 0.45);
      line.setOrigin(0, 0).setDepth(this.trackDepth);
      this.scene.tweens.add({
        targets: line,
        alpha: 0,
        delay: 900,
        duration: 1800,
        onComplete: () => line.destroy(),
      });
    }
  }

  /** Bees swarm after `target` for `ms` milliseconds. */
  swarm(target: Phaser.GameObjects.Sprite, ms: number): void {
    this.bees.startFollow(target);
    this.bees.start();
    this.scene.time.delayedCall(ms, () => this.bees.stop());
  }

  /** One exhaust puff at (px, py). */
  exhaust(px: number, py: number): void {
    this.exhaustPuffs.explode(1, px, py);
  }

  /** Petals burst from a flower bed the mower just ran over. */
  trample(tileX: number, tileY: number): void {
    const { x, y } = center(tileX, tileY);
    this.petals.explode(10, x, y);
  }

  sparkle(tileX: number, tileY: number): void {
    const { x, y } = center(tileX, tileY);
    this.sparkles.explode(7, x, y);
  }

  /** A small burst of sparkles at world pixel (px, py): a coin landing on the mower. */
  sparkleAt(px: number, py: number): void {
    this.sparkles.explode(4, px, py);
  }

  /** Mower soccer: a GOAL! burst of sparkles and confetti from the goal mouth at (px, py). */
  goalBurst(px: number, py: number): void {
    this.sparkles.explode(10, px, py);
    for (let i = 0; i < 18; i++) {
      this.confetti.explode(1, px + Phaser.Math.Between(-24, 24), py + Phaser.Math.Between(-10, 6));
    }
  }

  /** A soft puff where the mower bumped into something. */
  puff(px: number, py: number): void {
    this.dust.explode(6, px, py);
  }

  /**
   * A squirrel or rabbit poofs away (it is fine: off home for a while): a white cloud of round
   * puffs that swell and fade, with a sparkle.
   */
  poof(px: number, py: number): void {
    const puffs: [number, number, number][] = [
      [0, -2, 5],
      [-5, 1, 4],
      [5, 1, 4],
      [-2, 4, 3],
      [3, -5, 3],
    ];
    for (const [dx, dy, r] of puffs) {
      // A soft gray rim behind each white puff, so the cloud stands out from the grass.
      const c = this.scene.add
        .circle(px + dx, py + dy, r, 0xffffff, 1)
        .setStrokeStyle(1, 0xc8d0dc, 1)
        .setDepth(60);
      this.scene.tweens.add({
        targets: c,
        scale: { from: 0.5, to: 1.4 },
        y: c.y - 4,
        duration: 480,
        ease: 'Quad.easeOut',
        onComplete: () => c.destroy(),
      });
      // Solid at first, then gone.
      this.scene.tweens.add({ targets: c, alpha: 0, duration: 480, ease: 'Cubic.easeIn' });
    }
    this.sparkles.explode(3, px, py);
  }

  /** The mower comes apart (cartoonishly): bits fly up and tumble down. */
  breakApart(px: number, py: number): void {
    this.parts.explode(10, px, py);
    this.dust.explode(8, px, py);
  }

  shake(duration: number, intensity: number): void {
    if (this.reducedShake) return;
    this.scene.cameras.main.shake(duration, intensity);
  }

  /**
   * Lawn complete: the mowed stripes light up one column after another across the lawn, and
   * clippings and sparkles drift down from the top of the view (fewer with reduced motion).
   */
  celebrate(widthPx: number, heightPx: number): void {
    const cols = Math.round(widthPx / TILE_SIZE);
    for (let c = 0; c < cols; c++) {
      const glow = this.scene.add
        .rectangle(c * TILE_SIZE, 0, TILE_SIZE, heightPx, 0xffffff, 0)
        .setOrigin(0, 0)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(50);
      this.scene.tweens.chain({
        targets: glow,
        tweens: [
          { fillAlpha: 0.32, duration: 120, delay: c * 45, ease: 'Sine.easeOut' },
          { fillAlpha: 0, duration: 380, ease: 'Sine.easeIn' },
        ],
        onComplete: () => glow.destroy(),
      });
    }
    const cam = this.scene.cameras.main;
    const waves = this.reducedShake ? 1 : 4;
    for (let w = 0; w < waves; w++) {
      this.scene.time.delayedCall(150 + w * 160, () => {
        for (let i = 0; i < 10; i++) {
          this.confetti.explode(
            1,
            cam.scrollX + Phaser.Math.Between(0, cam.width),
            cam.scrollY - Phaser.Math.Between(0, 12),
          );
        }
      });
    }
  }
}

const center = (tx: number, ty: number) => ({
  x: (tx + 0.5) * TILE_SIZE,
  y: (ty + 0.5) * TILE_SIZE,
});
